'use client'

import { usePayStore } from '@/app/(dona)/layout'
import { FIELDNAMES } from '@/components/molecules/Form/utils/constants'
import PaymentValidator from '@/domain/PaymentValidator'
import { usePaymentExecutor } from '@/hooks/usePaymentExecutor'
import { usePaymentStateManager } from '@/hooks/usePaymentStateManager'
import { useWindowManager } from '@/hooks/useWindowManager'
import PaymentAPIService from '@/services/PaymentAPIService'
import {
  PAYMENT_STATES,
  RECURRENT_PAYMENT_STATUS_KEY,
  SINGLE_PAYMENT_STATUS_KEY,
} from '@/utils/constants'
import { derivePathPrefix } from '@/utils/derivePathPrefix'
import { handlePaymentError } from '@/utils/paymentErrorHandler'
import { useLocalStorageState } from 'ahooks'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const {
  DONATOR_FIRST_NAME,
  DONATOR_LAST_NAME,
  DONATOR_EMAIL,
  DONATOR_TAX_CODE,
  DONATOR_COMPANY_NAME,
  DONATOR_COMPANY_TAX_CODE,
  DONATOR_COMPANY_EMAIL,
  DONATION_IBAN_OWNER_FIRST_NAME,
  DONATION_IBAN_OWNER_LAST_NAME,
  DONATION_IBAN_OWNER_EMAIL,
  DONATION_IBAN_OWNER_TAX_CODE,
  DONATOR_PAYMENT_METHOD,
  DONATION_PAYMENT_METHOD_ON_RECURRENCE,
} = FIELDNAMES

export const usePayment = ({ paymentType = 'single', formData = {} }) => {
  const { setStore } = usePayStore()
  const router = useRouter()
  const orderRef = useRef(null)
  const hasRendered = useRef(false)
  const pathname = usePathname()
  const pathPrefix = useMemo(() => derivePathPrefix(pathname), [pathname])

  const { executeSinglePayment, executeRecurrentPayment } = usePaymentExecutor()

  const storageKey =
    paymentType === 'single'
      ? SINGLE_PAYMENT_STATUS_KEY
      : RECURRENT_PAYMENT_STATUS_KEY

  const [paymentState, setPaymentState] = useLocalStorageState(storageKey, {
    listenStorageChange: true,
    defaultValue: { state: PAYMENT_STATES.IDLE, method: '', info: {} },
  })

  const [token, setToken] = useState(null)
  const [paymentOptions, setPaymentOptions] = useState(null)
  const [approvalLink, setApprovalLink] = useState('')
  const [modalErrorIsOpen, setModalErrorIsOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isApiPending, setIsApiPending] = useState(false)

  const paypalClientId = process.env.paypalClientId
  const paymentClientId = process.env.paymentClientId

  const paypalInitialOptions = useMemo(() => {
    if (paymentType !== 'single') return null
    return {
      'client-id': paypalClientId,
      currency: 'EUR',
      intent: 'capture',
      'data-client-token': token,
      dataClientToken: token,
      components: ['hosted-fields', 'buttons', 'marks', 'funding-eligibility'],
      'disable-funding': 'mybank',
    }
  }, [paymentType, paypalClientId, token])

  const initializePaymentData = useCallback(async () => {
    if (paymentType !== 'single') return
    try {
      const [clientToken, options] = await Promise.all([
        PaymentAPIService.fetchClientToken(),
        PaymentAPIService.fetchPaymentOptions(),
      ])
      setToken(clientToken)
      setPaymentOptions(options)
    } catch (error) {
      handlePaymentError(error)
    }
  }, [paymentType])

  useEffect(() => {
    if (
      !hasRendered.current &&
      paymentType === 'single' &&
      (!token || !paymentOptions)
    ) {
      initializePaymentData()
      hasRendered.current = true
    }
  }, [paymentType, token, paymentOptions, initializePaymentData])

  const handleConfirmResult = useCallback(
    (resultData) => {
      const {
        success,
        data,
        error,
        paymentType: confirmPaymentType,
      } = resultData

      if (success) {
        orderRef.current = { ...orderRef.current, ...data }
        setPaymentState({
          state: PAYMENT_STATES.PAYMENT_SUCCESS,
          method: confirmPaymentType || paymentType,
          info: data,
        })

        setStore((prev) => ({
          ...prev,
          isSingleDonation: paymentType === 'single',
          isSingleDonationSuccessful: paymentType === 'single',
          isRecurrentPayment: paymentType !== 'single',
        }))

        const vals = typeof formData === 'function' ? formData() : formData
        const firstName =
          vals?.[DONATOR_FIRST_NAME] || vals?.[DONATOR_COMPANY_NAME] || ''
        router.push(
          `${pathPrefix}/successo${firstName ? `?firstName=${encodeURIComponent(firstName)}` : ''}`
        )
      } else {
        setPaymentState({
          state: PAYMENT_STATES.PAYMENT_ERROR,
          method: confirmPaymentType || paymentType,
          info: {
            error: error?.message || 'Errore durante la conferma del pagamento',
          },
        })
        setModalErrorIsOpen(true)
        setIsProcessing(false)
      }
    },
    [paymentType, setPaymentState, setStore, router, pathPrefix]
  )

  const handleRedirect = useCallback(
    (redirectData) => {
      const { status, redirectUrl } = redirectData
      const isSuccess = status === 'success'

      setPaymentState({
        state: isSuccess
          ? PAYMENT_STATES.PAYMENT_SUCCESS
          : PAYMENT_STATES.PAYMENT_ERROR,
        method: paymentType,
        info: { redirectUrl },
      })

      if (isSuccess) {
        router.push(redirectUrl || `${pathPrefix}/successo`)
      } else {
        setModalErrorIsOpen(true)
        setIsProcessing(false)
      }
    },
    [paymentType, setPaymentState, router, pathPrefix]
  )

  const { openWindow, startMonitoring, stopMonitoring, isWindowActive } =
    useWindowManager({
      onWindowClosed: (error) => {
        setPaymentState({
          state: PAYMENT_STATES.PAYMENT_ERROR,
          method: paymentState?.method || paymentType,
          info: { error: error.message },
        })
      },
      onRedirect: handleRedirect,
      onConfirmResult: handleConfirmResult,
    })

  const handlePaymentSuccess = useCallback(() => {
    stopMonitoring()
    setIsProcessing(true)

    const vals = typeof formData === 'function' ? formData() : formData

    if (paymentType === 'single') {
      setStore((prev) => ({
        ...prev,
        isSingleDonation: true,
        isSingleDonationSuccessful: true,
      }))
    } else {
      const recurrentData = {
        name:
          vals[DONATION_IBAN_OWNER_FIRST_NAME] ||
          vals[DONATOR_FIRST_NAME] ||
          vals[DONATOR_COMPANY_NAME] ||
          '',
        lastname:
          vals[DONATION_IBAN_OWNER_LAST_NAME] || vals[DONATOR_LAST_NAME] || '',
        taxcode:
          vals[DONATION_IBAN_OWNER_TAX_CODE] ||
          vals[DONATOR_TAX_CODE] ||
          vals[DONATOR_COMPANY_TAX_CODE] ||
          '',
        email:
          vals[DONATION_IBAN_OWNER_EMAIL] ||
          vals[DONATOR_EMAIL] ||
          vals[DONATOR_COMPANY_EMAIL] ||
          '',
      }

      setStore((prev) => ({
        ...prev,
        isRecurrentPayment: true,
        recursivePaymentState: paymentState,
        recurrentPaymentMethod:
          vals[DONATOR_PAYMENT_METHOD] ||
          vals[DONATION_PAYMENT_METHOD_ON_RECURRENCE] ||
          '',
        successBox: recurrentData,
      }))
    }
    const firstName =
      vals?.[DONATOR_FIRST_NAME] || vals?.[DONATOR_COMPANY_NAME] || ''
    router.push(
      `${pathPrefix}/successo${firstName ? `?firstName=${encodeURIComponent(firstName)}` : ''}`
    )
  }, [
    paymentType,
    setStore,
    router,
    paymentState,
    stopMonitoring,
    formData,
    pathPrefix,
  ])

  usePaymentStateManager({
    paymentState,
    setPaymentState,
    setModalErrorIsOpen,
    isApiPending,
    onSuccess: handlePaymentSuccess,
    onError: () => {
      stopMonitoring()
      setIsProcessing(false)
    },
  })

  const createPaymentOrder = useCallback(
    async (options = {}) => {
      try {
        const vals = typeof formData === 'function' ? formData() : formData
        const validation = PaymentValidator.validatePaymentRequest(vals, {
          showToast: true,
        })
        if (!validation.isValid)
          return { success: false, error: validation.firstError }

        setIsProcessing(true)
        setPaymentState({
          state: PAYMENT_STATES.WAITING_FOR_PAYMENT,
          method: paymentType,
          info: { ...options },
        })
        setModalErrorIsOpen(PAYMENT_STATES.WAITING_FOR_PAYMENT)

        setIsApiPending(true)
        const orderResult =
          paymentType === 'single'
            ? await executeSinglePayment(vals, { ...options, pathPrefix })
            : await executeRecurrentPayment(vals)
        setIsApiPending(false)

        orderRef.current = orderResult

        if (!orderResult.success) {
          setIsProcessing(false)
          setPaymentState({
            state: PAYMENT_STATES.PAYMENT_ERROR,
            method: paymentType,
            info: { error: orderResult.error },
          })
          return orderResult
        }

        const source = options?.paymentSource || ''
        const isHosted = source === 'creditcard' || source === 'creditcard-2022'

        if (source === 'paypal' || (isHosted && !orderResult.approvalLink)) {
          if (isHosted) setIsProcessing(true)
          else setIsProcessing(false)
          return orderResult
        }

        setIsProcessing(false)
        if (orderResult.approvalLink) {
          setApprovalLink(orderResult.approvalLink)
          setPaymentState((prev) => ({
            ...prev,
            info: {
              ...prev.info,
              approvalLink: orderResult.approvalLink,
              ckey: orderResult.ckey,
              orderID: orderResult.orderID,
            },
          }))
        }
        return orderResult
      } catch (error) {
        setIsApiPending(false)
        setIsProcessing(false)
        handlePaymentError(error)
        setPaymentState({
          state: PAYMENT_STATES.PAYMENT_ERROR,
          method: paymentType,
          info: { error: error.message },
        })
        return { success: false, error }
      }
    },
    [
      formData,
      paymentType,
      setPaymentState,
      executeSinglePayment,
      executeRecurrentPayment,
      pathPrefix,
    ]
  )

  const openPaymentPopup = useCallback(
    async (url) => {
      const result = await openWindow(url)
      if (!result.success) {
        setPaymentState({
          state: PAYMENT_STATES.PAYMENT_ERROR,
          method: paymentType,
          info: { error: result.message || result.error },
        })
        return result
      }
      if (result.windowDetected) startMonitoring(result.window)
      return result
    },
    [openWindow, startMonitoring, setPaymentState, paymentType]
  )

  const completePayment = useCallback(
    async (options = {}) => {
      const res = await createPaymentOrder(options)
      if (res.success && res.approvalLink) {
        const winRes = await openPaymentPopup(res.approvalLink)
        return { ...res, windowResult: winRes }
      }
      return res
    },
    [createPaymentOrder, openPaymentPopup]
  )

  const handleOnApprove = useCallback(
    async (data) => {
      const orderData = { ...data, ...orderRef.current }
      if (!orderData.ckey) {
        setPaymentState({
          state: PAYMENT_STATES.PAYMENT_ERROR,
          method: paymentType,
          info: { error: 'Missing ckey' },
        })
        setModalErrorIsOpen(true)
        return data
      }
      const vals = typeof formData === 'function' ? formData() : formData
      const firstName =
        vals?.[DONATOR_FIRST_NAME] || vals?.[DONATOR_COMPANY_NAME] || ''
      router.push(
        `${pathPrefix}/confirm?ckey=${orderData.ckey}&token=${orderData.token || ''}&orderID=${orderData.orderID || ''}&firstName=${encodeURIComponent(firstName)}`
      )
      return data
    },
    [router, paymentType, setPaymentState, pathPrefix]
  )

  const handleOnError = useCallback(
    async (error) => {
      handlePaymentError(error)
      setIsProcessing(false)
      setPaymentState({
        state: PAYMENT_STATES.PAYMENT_ERROR,
        method: paymentState?.method || paymentType,
        info: { error: error.message },
      })
      return error
    },
    [paymentState?.method, paymentType, setPaymentState]
  )

  return {
    paymentState,
    setPaymentState,
    token,
    paymentOptions,
    paypalInitialOptions,
    paymentClientId,
    paypalClientId,
    approvalLink,
    setApprovalLink,
    modalErrorIsOpen,
    setModalErrorIsOpen,
    isWindowActive,
    isProcessing,
    createPaymentOrder,
    openPaymentPopup,
    completePayment,
    handleOnApprove,
    handleOnError,
    handleOnCreateOrder: createPaymentOrder,
    handleCreateRecurrentPayment: completePayment,
    handleOnCreateAlternativeOrder: (paymentSource, searchParams) =>
      completePayment({ paymentSource, searchParams, returnLink: true }),
  }
}

export default usePayment
